<template>
  <section
    class="ca-slider-catalog"
    :class="resolvedTheme ? `ca-slider-catalog--${resolvedTheme}` : ''"
    :aria-label="resolvedTitle"
  >
    <div class="ca-bullet-container ca-slider-catalog__inner">
      <div class="ca-slider-catalog__header">
        <CaModuleMessage
          :title="resolvedTitle"
          :title-heading-type="resolvedTitleHeadingType"
          :extra-title="resolvedExtraText"
          :extra-description="resolvedExtraDescription"
          :theme="moduleMessageTheme"
        />
      </div>

      <!-- Content container -->
      <div class="ca-slider-catalog__container">
        <!-- Filter chips -->
        <div
          v-if="resolvedCategories.length"
          class="ca-slider-catalog__chips-nav"
        >
          <IconButton
            v-if="chipsOverflow"
            type="inverted"
            variant="tertiary"
            class="ca-slider-catalog__chips-arrow"
            :disabled="!chipsFadeLeft"
            aria-label="Anterior"
            @clicked="scrollChipsPrev"
          >
            <template #trailingIcon>
              <ChevronLeft />
            </template>
          </IconButton>

          <div
            ref="chipsRef"
            class="ca-slider-catalog__chips"
            :class="{
              'ca-slider-catalog__chips--fade-left': chipsFadeLeft,
              'ca-slider-catalog__chips--fade-right': chipsFadeRight,
            }"
            role="tablist"
            aria-label="Categorias de produtos"
            @keydown="handleChipsKeydown"
          >
            <CaSliderChip
              v-for="(cat, idx) in resolvedCategories"
              :key="idx"
              class="paragraph-medium"
              :label="cat.label"
              :active="activeCategory === idx"
              @click="selectCategory(idx)"
            />
          </div>

          <IconButton
            v-if="chipsOverflow"
            type="inverted"
            variant="tertiary"
            class="ca-slider-catalog__chips-arrow"
            :disabled="!chipsFadeRight"
            aria-label="Seguinte"
            @clicked="scrollChipsNext"
          >
            <template #trailingIcon>
              <ChevronRight />
            </template>
          </IconButton>
        </div>

        <!-- Slider -->
        <div
          ref="sliderRef"
          class="ca-slider-catalog__slider"
          :class="{
            'ca-slider-catalog__slider--fade-left':
              visibleCards.length > 1 && canScrollPrev,
            'ca-slider-catalog__slider--fade-right':
              visibleCards.length > 1 && canScrollNext,
          }"
          role="region"
          aria-roledescription="carousel"
          aria-label="Carrossel de produtos"
        >
          <div class="ca-slider-catalog__track">
            <template
              v-for="(card, idx) in visibleCards"
              :key="`${activeCategory}-${idx}`"
            >
              <CaSliderSimulatorCard
                v-if="card.kind === 'simulator'"
                :label="card.label"
                :title="card.title"
                :title-heading-type="card.titleHeadingType"
                :image-src="card.imageSrc"
                :image-alt="card.imageAlt"
                :simulator-label="card.ctaText || 'Simulador'"
                :simulator-url="card.simulatorUrl"
                :simulator-target="card.simulatorTarget"
                :buttons="card.buttons"
                class="ca-slider-catalog__card ca-slider-catalog__card--wide"
                role="group"
                aria-roledescription="slide"
                :aria-label="`Cartão ${idx + 1} de ${visibleCards.length}. ${card.title}`"
                @action="handleCardAction(card)"
              />
              <CaSliderContentCard
                v-else
                :label="card.label"
                :title="card.title"
                :title-heading-type="card.titleHeadingType"
                :image-src="card.imageSrc"
                :image-alt="card.imageAlt"
                :aria-label="
                  card.ariaLabel ||
                  `Cartão ${idx + 1} de ${visibleCards.length}. ${card.title}`
                "
                class="ca-slider-catalog__card"
                role="group"
                aria-roledescription="slide"
                @action="handleCardAction(card)"
              />
            </template>
            <div class="ca-slider-catalog__spacer" aria-hidden="true"></div>
          </div>
        </div>

        <!-- Page dots (mobile only) -->
        <CaPageControl
          v-if="visibleCards.length > 1"
          class="ca-slider-catalog__dots"
          :count="visibleCards.length"
          :model-value="activeSlide"
          @update:model-value="scrollToSlide"
        />

        <!-- Footer: link + navigation arrows -->
        <div class="ca-slider-catalog__footer">
          <BaseButton
            v-if="resolvedLinkLabel"
            :label="resolvedLinkLabel"
            class="inverted tertiary ca-slider-catalog__link"
            @click="handleLinkClick"
          >
            <template #leadingIcon>
              <img v-if="linkIcon" :src="linkIcon" alt="" />
              <ArrowRight v-else />
            </template>
          </BaseButton>
          <div class="ca-slider-catalog__arrows">
            <IconButton
              type="inverted"
              variant="tertiary"
              class="ca-slider-catalog__arrow"
              :class="{ 'ca-slider-catalog__arrow--disabled': !canScrollPrev }"
              :disabled="!canScrollPrev"
              aria-label="Anterior"
              @clicked="scrollPrev"
            >
              <template #trailingIcon>
                <ChevronLeft />
              </template>
            </IconButton>
            <IconButton
              type="inverted"
              variant="tertiary"
              class="ca-slider-catalog__arrow"
              :class="{ 'ca-slider-catalog__arrow--disabled': !canScrollNext }"
              :disabled="!canScrollNext"
              aria-label="Seguinte"
              @clicked="scrollNext"
            >
              <template #trailingIcon>
                <ChevronRight />
              </template>
            </IconButton>
          </div>
        </div>
      </div>
    </div>
    <div
      aria-live="polite"
      aria-atomic="true"
      class="ca-slider-catalog__sr-only"
    >
      {{ liveAnnouncement }}
    </div>
  </section>
</template>

<script setup>
import {
  ref,
  computed,
  onMounted,
  onBeforeUnmount,
  nextTick,
  watch,
} from "vue";
import { BaseButton, IconButton } from "ca-online-design-system";
import CaSliderContentCard from "../../base/cards/CaSliderContentCard.vue";
import CaSliderSimulatorCard from "../../base/cards/CaSliderSimulatorCard.vue";
import CaSliderChip from "../../base/tags/CaSliderChip.vue";
import CaPageControl from "../../base/CaPageControl.vue";
import CaModuleMessage from "../../base/moduleMessage/CaModuleMessage.vue";
import ChevronLeft from "@assets/chevron-left.svg";
import ChevronRight from "@assets/chevron-right.svg";
import ArrowRight from "@assets/arrow-right.svg";

defineOptions({
  name: "CaSliderCatalogModule",
});

const props = defineProps({
  /** Raw Sitecore model (data-props JSON). When provided, takes precedence. */
  model: {
    type: Object,
    default: null,
  },
  /** Module title. */
  title: {
    type: String,
    default: "",
  },
  /** Semantic element used for the module title. */
  titleHeadingType: {
    type: String,
    default: "H2",
  },
  /** Theme identifier from backend. */
  theme: {
    type: String,
    default: "",
  },
  /** Extra text below the title. */
  extraText: {
    type: String,
    default: "",
  },
  /** Extra description below the extra text. */
  extraDescription: {
    type: String,
    default: "",
  },
  /** Filter categories (tabs). Each item: { label: string, cards: Array } */
  categories: {
    type: Array,
    default: () => [],
  },
  /** Backend "blocks" array — alias for categories. */
  blocks: {
    type: Array,
    default: () => [],
  },
  /** Label for the bottom-left link button. */
  linkLabel: {
    type: String,
    default: "",
  },
  /** Icon URL for the link button (from Sitecore). */
  linkIcon: {
    type: String,
    default: "",
  },
});

const emit = defineEmits(["card-action", "link-click", "category-change"]);

// --- Backend → internal mapping ---
function mapCard(card) {
  return {
    ...card,
    imageSrc: card.imageSrc || card.imageUrl || "",
    label: card.label || "",
    title: card.title || "",
    imageAlt: card.imageAlt || card.title || "",
    ctaUrl: card.ctaUrl || "",
    ctaTarget: card.ctaTarget || "",
  };
}

function mapBlocks(blocks) {
  return (blocks || []).map((block) => ({
    ...block,
    label: block.label || "",
    footerText: block.footerText || "",
    footerUrl: block.footerUrl || "",
    footerTarget: block.footerTarget || "",
    cards: (block.cards || []).map(mapCard),
  }));
}

const resolvedTitle = computed(() => props.model?.title || props.title);
const resolvedTitleHeadingType = computed(
  () => props.model?.titleHeadingType || props.titleHeadingType,
);
const resolvedExtraText = computed(
  () => props.model?.extraText || props.extraText,
);
const resolvedExtraDescription = computed(
  () => props.model?.extraDescription || props.extraDescription,
);
const resolvedTheme = computed(() => {
  const t = props.model?.theme || props.theme;
  return t ? t.toLowerCase() : "";
});
const moduleMessageTheme = computed(() =>
  resolvedTheme.value === "white" ? "default" : "inverted",
);

const resolvedCategories = computed(() => {
  let cats = [];
  if (props.model?.blocks) cats = mapBlocks(props.model.blocks);
  else if (props.blocks.length) cats = mapBlocks(props.blocks);
  else if (props.categories.length) cats = props.categories;
  // Blocks without cards produce no chip
  return cats.filter((cat) => (cat.cards || []).length > 0);
});

// --- Category selection ---
const activeCategory = ref(0);
const liveAnnouncement = ref("");

// Visible cards: the active category's own cards
const visibleCards = computed(
  () => resolvedCategories.value[activeCategory.value]?.cards || [],
);

// Per-category footer: use active category's footerText/footerUrl, or fall back to prop
const activeCategoryData = computed(
  () => resolvedCategories.value[activeCategory.value] || {},
);
const resolvedLinkLabel = computed(
  () => activeCategoryData.value.footerText || props.linkLabel,
);
const resolvedLinkUrl = computed(
  () => activeCategoryData.value.footerUrl || "",
);
const resolvedLinkTarget = computed(
  () => activeCategoryData.value.footerTarget || "_self",
);

function selectCategory(idx) {
  activeCategory.value = idx;
  emit("category-change", idx);

  const cat = resolvedCategories.value[idx];
  liveAnnouncement.value = `${cat?.label || ""}, selecionado. Conteúdo atualizado.`;

  nextTick(() => {
    const container = chipsRef.value;
    if (!container) return;
    const chip = container.children[idx];
    if (!chip) return;
    container.scrollTo({
      left: chip.offsetLeft - container.offsetLeft - 24,
      behavior: "smooth",
    });
  });
}

function handleCardAction(card) {
  emit("card-action", card);
  if (card.ctaUrl) {
    window.open(card.ctaUrl, card.ctaTarget || "_self");
  }
}

function handleLinkClick() {
  emit("link-click");
  if (resolvedLinkUrl.value) {
    window.open(resolvedLinkUrl.value, resolvedLinkTarget.value);
  }
}

function handleChipsKeydown(e) {
  const cats = resolvedCategories.value;
  if (!cats.length) return;
  let newIdx = activeCategory.value;
  if (e.key === "ArrowRight" || e.key === "ArrowDown") {
    e.preventDefault();
    newIdx = (activeCategory.value + 1) % cats.length;
  } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
    e.preventDefault();
    newIdx = (activeCategory.value - 1 + cats.length) % cats.length;
  } else if (e.key === "Home") {
    e.preventDefault();
    newIdx = 0;
  } else if (e.key === "End") {
    e.preventDefault();
    newIdx = cats.length - 1;
  } else {
    return;
  }
  selectCategory(newIdx);
  nextTick(() => {
    const container = chipsRef.value;
    if (container?.children[newIdx]) container.children[newIdx].focus();
  });
}

watch(visibleCards, () => {
  cancelSliderAnimation();
  targetSlideIndex = null;
  nextTick(() => {
    const el = sliderRef.value;
    if (el) {
      el.scrollTo({ left: 0, behavior: "auto" });
      activeSlide.value = 0;
      updateScrollState();
    }
  });
});

// --- Chips scroll fade ---
const chipsRef = ref(null);
const chipsFadeLeft = ref(false);
const chipsFadeRight = ref(false);
const chipsOverflow = ref(false);

function updateChipsScroll() {
  const el = chipsRef.value;
  if (!el) return;
  chipsFadeLeft.value = el.scrollLeft > 0;
  chipsFadeRight.value = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
  chipsOverflow.value = el.scrollWidth > el.clientWidth;
}

function scrollChipsPrev() {
  const el = chipsRef.value;
  if (!el) return;
  el.scrollBy({ left: -200, behavior: "smooth" });
}

function scrollChipsNext() {
  const el = chipsRef.value;
  if (!el) return;
  el.scrollBy({ left: 200, behavior: "smooth" });
}

// --- Slider scrolling ---
const sliderRef = ref(null);
const canScrollPrev = ref(false);
const canScrollNext = ref(false);
const activeSlide = ref(0);
let sliderAnimationFrame = null;
let targetSlideIndex = null;

function getCards() {
  return Array.from(
    sliderRef.value?.querySelectorAll(".ca-slider-catalog__card") || [],
  );
}

function getScrollPaddingLeft(el) {
  return parseFloat(getComputedStyle(el).scrollPaddingLeft) || 0;
}

function getCardTarget(el, card) {
  const target =
    el.scrollLeft +
    card.getBoundingClientRect().left -
    el.getBoundingClientRect().left -
    getScrollPaddingLeft(el);
  return Math.max(0, Math.min(target, el.scrollWidth - el.clientWidth));
}

function getNearestCardIndex(el = sliderRef.value) {
  const cards = getCards();
  if (!el || !cards.length) return 0;

  let nearestIndex = 0;
  let nearestDistance = Infinity;
  cards.forEach((card, index) => {
    const distance = Math.abs(getCardTarget(el, card) - el.scrollLeft);
    if (distance < nearestDistance) {
      nearestDistance = distance;
      nearestIndex = index;
    }
  });
  return nearestIndex;
}

function updateScrollState() {
  const el = sliderRef.value;
  if (!el) return;
  const epsilon = 2;
  canScrollPrev.value = el.scrollLeft > epsilon;
  // Account for the spacer element at the end of the track
  const spacer = el.querySelector(".ca-slider-catalog__spacer");
  const spacerWidth = spacer ? spacer.offsetWidth : 0;
  canScrollNext.value =
    el.scrollLeft + el.clientWidth < el.scrollWidth - spacerWidth - epsilon;

  activeSlide.value = getNearestCardIndex(el);
}

function cancelSliderAnimation() {
  if (sliderAnimationFrame !== null) {
    cancelAnimationFrame(sliderAnimationFrame);
    sliderAnimationFrame = null;
  }
  if (sliderRef.value) sliderRef.value.style.scrollSnapType = "";
}

function scrollToPosition(el, target, duration = 450) {
  cancelSliderAnimation();

  const start = el.scrollLeft;
  const clampedTarget = Math.max(
    0,
    Math.min(target, el.scrollWidth - el.clientWidth),
  );
  if (
    Math.abs(clampedTarget - start) < 1 ||
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  ) {
    el.scrollTo({ left: clampedTarget, behavior: "auto" });
    targetSlideIndex = null;
    updateScrollState();
    return;
  }

  const startTime = performance.now();
  el.style.scrollSnapType = "none";

  function step(now) {
    const t = Math.min((now - startTime) / duration, 1);
    const ease = 1 - Math.pow(1 - t, 3);
    el.scrollLeft = start + (clampedTarget - start) * ease;
    if (t < 1) {
      sliderAnimationFrame = requestAnimationFrame(step);
    } else {
      sliderAnimationFrame = null;
      el.scrollLeft = clampedTarget;
      el.style.scrollSnapType = "";
      targetSlideIndex = null;
      updateScrollState();
    }
  }

  sliderAnimationFrame = requestAnimationFrame(step);
}

function scrollToCard(idx) {
  const el = sliderRef.value;
  const cards = getCards();
  const clampedIndex = Math.max(0, Math.min(idx, cards.length - 1));
  const card = cards[clampedIndex];
  if (!el || !card) return;
  targetSlideIndex = clampedIndex;
  scrollToPosition(el, getCardTarget(el, card));
}

function scrollPrev() {
  scrollToCard((targetSlideIndex ?? getNearestCardIndex()) - 1);
}

function scrollNext() {
  scrollToCard((targetSlideIndex ?? getNearestCardIndex()) + 1);
}

function scrollToSlide(idx) {
  scrollToCard(idx);
}

// --- Drag-to-scroll (desktop only) ---
let isDragging = false;
let dragStartX = 0;
let chipsRo = null;
let sliderRo = null;

function onPointerDown(e) {
  if (e.pointerType === "touch") {
    cancelSliderAnimation();
    targetSlideIndex = null;
    return;
  }
  if (e.target instanceof Element && e.target.closest("button, a, input, select, textarea")) {
    return;
  }
  if (e.button !== 0) return;
  cancelSliderAnimation();
  targetSlideIndex = null;
  isDragging = true;
  dragStartX = e.pageX;
  e.currentTarget.setPointerCapture?.(e.pointerId);
}

function onPointerMove(e) {
  if (!isDragging) return;
}

function onPointerUp(e) {
  if (!isDragging) return;
  isDragging = false;
  e.currentTarget.releasePointerCapture?.(e.pointerId);
  const delta = e.pageX - dragStartX;
  const threshold = 30;
  if (delta < -threshold) {
    scrollNext();
  } else if (delta > threshold) {
    scrollPrev();
  }
}

function onPointerCancel() {
  isDragging = false;
}

function onWheel() {
  cancelSliderAnimation();
  targetSlideIndex = null;
}

onMounted(() => {
  const el = sliderRef.value;
  if (el) {
    el.addEventListener("scroll", updateScrollState, { passive: true });
    el.addEventListener("pointerdown", onPointerDown);
    el.addEventListener("pointermove", onPointerMove);
    el.addEventListener("pointerup", onPointerUp);
    el.addEventListener("pointercancel", onPointerCancel);
    el.addEventListener("wheel", onWheel, { passive: true });
    el.addEventListener("dragstart", preventNativeDrag);
    updateScrollState();
  }
  const chips = chipsRef.value;
  if (chips) {
    chips.addEventListener("scroll", updateChipsScroll, { passive: true });
    updateChipsScroll();
  }
  chipsRo = new ResizeObserver(updateChipsScroll);
  if (chips) chipsRo.observe(chips);
  sliderRo = new ResizeObserver(updateScrollState);
  if (el) sliderRo.observe(el);
});

function preventNativeDrag(e) {
  e.preventDefault();
}

onBeforeUnmount(() => {
  cancelSliderAnimation();
  const el = sliderRef.value;
  if (el) {
    el.removeEventListener("scroll", updateScrollState);
    el.removeEventListener("pointerdown", onPointerDown);
    el.removeEventListener("pointermove", onPointerMove);
    el.removeEventListener("pointerup", onPointerUp);
    el.removeEventListener("pointercancel", onPointerCancel);
    el.removeEventListener("wheel", onWheel);
    el.removeEventListener("dragstart", preventNativeDrag);
  }
  const chips = chipsRef.value;
  if (chips) {
    chips.removeEventListener("scroll", updateChipsScroll);
  }
  if (chipsRo) {
    chipsRo.disconnect();
    chipsRo = null;
  }
  if (sliderRo) {
    sliderRo.disconnect();
    sliderRo = null;
  }
});
</script>

<style scoped lang="scss">
@use "sass:math";

.ca-slider-catalog {
  width: 100%;
  padding-top: 48px;
  padding-bottom: 48px;

  @include ca-bp("sm") {
    padding-top: 56px;
    padding-bottom: 56px;
  }

  @include ca-bp("lg") {
    padding-top: 64px;
    padding-bottom: 64px;
  }

  background:
    linear-gradient(
      270deg,
      rgba(155, 193, 179, 0.2) 0%,
      rgba(4, 61, 40, 0.2) 52.01%
    ),
    linear-gradient(90deg, rgb(4, 61, 40) 0%, rgb(4, 61, 40) 100%);
  overflow: clip;

  &--white {
    background:
      linear-gradient(
        270deg,
        var(--ca-color-neutrals-on-background-low) 0%,
        var(--ca-color-neutrals-on-background) 52.01%
      ),
      var(--ca-color-neutrals-on-background);
  }

  &__inner {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: var(--size-10);
  }

  &__header {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: var(--size-7);
    width: 100%;

    :deep(.module-message__title) {
      color: var(--ca-color-text-inverted);
    }
  }

  &__container {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    gap: var(--size-6);
    width: 100%;

    @include ca-bp("lg") {
      gap: var(--size-8);
    }
  }

  // --- Chips ---
  &__chips-nav {
    display: flex;
    align-items: center;
    gap: var(--size-4);
    width: 100%;
    position: relative;
  }

  &__chips-arrow {
    display: none !important;
    flex-shrink: 0;
    border-radius: 100px !important;
    width: 48px !important;
    height: 48px !important;
    background-color: var(
      --ca-color-neutrals-inverted-10,
      rgba(255, 255, 255, 0.1)
    ) !important;
    box-shadow: 0 2px 6px 0 rgba(0, 0, 0, 0.1);

    @include ca-bp("lg") {
      display: inline-flex !important;
    }

    &:hover {
      background-color: var(
        --ca-color-neutrals-inverted-30,
        rgba(255, 255, 255, 0.3)
      ) !important;
    }

    &:active {
      background-color: var(
        --ca-color-neutrals-inverted-60,
        rgba(255, 255, 255, 0.6)
      ) !important;
    }

    &:disabled {
      opacity: 0.5;
      pointer-events: none;
    }
  }

  &__chips {
    display: flex;
    gap: var(--size-4);
    align-items: flex-start;
    overflow-x: auto;
    scrollbar-width: none;
    -ms-overflow-style: none;
    width: calc(100% + ca-margin("base") * 2);
    margin-left: calc(ca-margin("base") * -1);
    margin-right: calc(ca-margin("base") * -1);
    padding-left: ca-margin("base");
    padding-right: ca-margin("base");
    min-width: 0;
    padding-top: 4px;
    padding-bottom: 4px;

    @include ca-bp("sm") {
      width: calc(100% + ca-margin("sm") * 2);
      margin-left: calc(ca-margin("sm") * -1);
      margin-right: calc(ca-margin("sm") * -1);
      padding-left: ca-margin("sm");
      padding-right: ca-margin("sm");
    }

    @include ca-bp("md") {
      width: calc(100% + ca-margin("md") * 2);
      margin-left: calc(ca-margin("md") * -1);
      margin-right: calc(ca-margin("md") * -1);
      padding-left: ca-margin("md");
      padding-right: ca-margin("md");
    }

    @include ca-bp("lg") {
      width: 100%;
      margin-left: 0;
      margin-right: 0;
      padding-left: 4px;
      padding-right: 4px;

      &--fade-right {
        mask-image: linear-gradient(
          to right,
          black calc(100% - var(--size-10)),
          transparent
        );
      }

      &--fade-left {
        mask-image: linear-gradient(
          to left,
          black calc(100% - var(--size-10)),
          transparent
        );
      }

      &--fade-left#{&}--fade-right {
        mask-image: linear-gradient(
          to right,
          transparent,
          black var(--size-10),
          black calc(100% - var(--size-10)),
          transparent
        );
      }
    }

    &::-webkit-scrollbar {
      display: none;
    }
  }

  // --- Slider ---
  &__slider {
    width: calc(100% + ca-margin("base") * 2);
    margin-left: calc(ca-margin("base") * -1);
    margin-right: calc(ca-margin("base") * -1);
    padding-left: ca-margin("base");
    scroll-padding-left: ca-margin("base");
    overflow-x: auto;
    scrollbar-width: none;
    -ms-overflow-style: none;
    scroll-snap-type: x mandatory;
    overscroll-behavior-inline: contain;

    @include ca-bp("sm") {
      width: calc(100% + ca-margin("sm") * 2);
      margin-left: calc(ca-margin("sm") * -1);
      margin-right: calc(ca-margin("sm") * -1);
      padding-left: ca-margin("sm");
      scroll-padding-left: ca-margin("sm");
    }

    @include ca-bp("md") {
      width: calc(100% + ca-margin("md") * 2);
      margin-left: calc(ca-margin("md") * -1);
      margin-right: calc(ca-margin("md") * -1);
      padding-left: ca-margin("md");
      scroll-padding-left: ca-margin("md");
    }

    @include ca-bp("lg") {
      width: calc(100% + ca-margin("lg") * 2);
      margin-left: calc(ca-margin("lg") * -1);
      margin-right: calc(ca-margin("lg") * -1);
      padding-left: ca-margin("lg");
      scroll-padding-left: ca-margin("lg");
    }

    @include ca-bp("lg2") {
      width: calc(100% + ca-margin("lg2") * 2);
      margin-left: calc(ca-margin("lg2") * -1);
      margin-right: calc(ca-margin("lg2") * -1);
      padding-left: ca-margin("lg2");
      scroll-padding-left: ca-margin("lg2");
    }

    @include ca-bp("xl") {
      width: calc(100% + ca-margin("xl") * 2);
      margin-left: calc(ca-margin("xl") * -1);
      margin-right: calc(ca-margin("xl") * -1);
      padding-left: ca-margin("xl");
      scroll-padding-left: ca-margin("xl");

      &--fade-right {
        mask-image: linear-gradient(
          to right,
          black calc(100% - var(--size-10)),
          transparent
        );
      }

      &--fade-left {
        mask-image: linear-gradient(
          to left,
          black calc(100% - var(--size-10)),
          transparent
        );
      }

      &--fade-left#{&}--fade-right {
        mask-image: linear-gradient(
          to right,
          transparent,
          black var(--size-10),
          black calc(100% - var(--size-10)),
          transparent
        );
      }
    }

    &::-webkit-scrollbar {
      display: none;
    }
  }

  &__track {
    display: flex;
    gap: ca-gutter("base");
    align-items: center;

    @include ca-bp("sm") {
      gap: ca-gutter("sm");
    }

    @include ca-bp("lg") {
      gap: ca-gutter("md");
    }
  }

  &__spacer {
    flex-shrink: 0;
    display: block;
    min-height: 1px;
    width: 1px;

    @include ca-bp("sm") {
      width: 1px;
    }

    @include ca-bp("lg") {
      width: calc(ca-margin("md") - ca-gutter("md"));
    }

    @include ca-bp("lg") {
      width: calc(ca-margin("lg") - ca-gutter("md"));
    }

    @include ca-bp("lg2") {
      width: calc(ca-margin("lg2") - ca-gutter("md"));
    }

    @include ca-bp("xl") {
      width: calc(ca-margin("xl") - ca-gutter("md"));
    }
  }

  &__card {
    flex-shrink: 0;
    width: calc(100vw - 48px);
    scroll-snap-align: start;
    scroll-snap-stop: always;
    user-select: none;

    @include ca-bp("md") {
      width: 645px;
    }

    @include ca-bp("lg") {
      width: calc(50% - #{math.div(ca-margin("lg"), 2)});
    }

    @include ca-bp("lg2") {
      width: calc(50% - #{math.div(ca-margin("lg2"), 2)});
    }

    @include ca-bp("xl") {
      width: calc(50% - #{math.div(ca-margin("xl"), 2)});
    }

    &--wide {
      @include ca-bp("md") {
        width: 811px;
      }

      @include ca-bp("lg") {
        width: calc(66.666667% - #{math.div(ca-margin("lg") * 2, 3)});
      }

      @include ca-bp("lg2") {
        width: calc(66.666667% - #{math.div(ca-margin("lg2") * 2, 3)});
      }

      @include ca-bp("xl") {
        width: calc(66.666667% - #{math.div(ca-margin("xl") * 2, 3)});
      }
    }
  }

  // --- Footer ---
  &__footer {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: var(--size-4);
    width: 100%;

    @include ca-bp("sm") {
      justify-content: space-between;
    }
  }

  &__link {
    flex: 0 1 auto;
    min-width: 0;
    max-width: 100%;
    height: auto;
    min-height: 48px;
    padding-block: var(--size-2);

    :deep(.button-text) {
      min-width: 0;
      overflow: visible;
      text-overflow: clip;
      white-space: normal;
      overflow-wrap: anywhere;
      word-break: normal;
    }

    :deep(.ml-size-3) {
      flex: 0 0 auto;
    }
  }

  &__arrows {
    display: none;
    flex: 0 0 auto;
    gap: var(--size-4);
    align-items: flex-start;

    @include ca-bp("sm") {
      display: flex;
    }
  }

  &__arrow {
    border-radius: 100px !important;
    width: 48px !important;
    height: 48px !important;
    background-color: var(
      --ca-color-neutrals-inverted-10,
      rgba(255, 255, 255, 0.1)
    ) !important;
    box-shadow: 0 2px 6px 0 rgba(0, 0, 0, 0.1);

    &:hover {
      background-color: var(
        --ca-color-neutrals-inverted-30,
        rgba(255, 255, 255, 0.3)
      ) !important;
    }

    &:active {
      background-color: var(
        --ca-color-neutrals-inverted-60,
        rgba(255, 255, 255, 0.6)
      ) !important;
    }

    &--disabled {
      opacity: 0.5;
    }
  }

  // --- Page dots ---
  &__dots {
    width: 100%;
    margin-top: -8px;

    @include ca-bp("sm") {
      display: none !important;
    }
  }

  &__sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }
}
</style>

<style lang="scss">
.ca-slider-catalog__arrow--disabled {
  pointer-events: none;
}
</style>
